import type { Workspace, WorkspaceRole } from '../types/workspace'
import { isPersonalWorkspace } from './workspaceKind'
import { isPhoneOnlyIdentity } from './userIdentity'

export interface HelperCheckMembership {
  role?: string | null
  status?: string | null
  workspaceId?: string
}

export interface HelperCheckOptions {
  userProfile?: { role?: string | null } | null
  currentUser?: { phoneNumber?: string | null; email?: string | null; isAnonymous?: boolean } | null
  memberships?: HelperCheckMembership[] | null
  workspaces?: Workspace[] | null
  rolesByWorkspaceId?: Map<string, WorkspaceRole> | Record<string, WorkspaceRole> | null
  activeRole?: WorkspaceRole | null
}

/**
 * TM16: Helper is a per-workspace role, not an account type.
 * An account is helper-only if and only if every ACTIVE membership is HELPER.
 * Old accounts with userProfile.role === 'HELPER' are judged by their memberships (TM14).
 * The web console is blocked ONLY for helper-only accounts.
 */
export function isHelperOnlyAccount(options: HelperCheckOptions = {}): boolean {
  const { userProfile, currentUser, memberships, workspaces, rolesByWorkspaceId, activeRole } = options

  // 1. If explicit memberships array is provided, evaluate active memberships
  if (Array.isArray(memberships) && memberships.length > 0) {
    const active = memberships.filter((m) => (m.status ?? 'ACTIVE') === 'ACTIVE')
    if (active.length > 0) {
      return active.every((m) => m.role === 'HELPER')
    }
  }

  // 2. If workspaces array is provided, evaluate active workspaces
  if (Array.isArray(workspaces) && workspaces.length > 0) {
    // If any workspace is personal, personal space is owned (not helper)
    const nonPersonal = workspaces.filter((ws) => !isPersonalWorkspace(ws))
    if (nonPersonal.length < workspaces.length) {
      // User has a personal space -> not helper-only
      return false
    }

    if (workspaces.length > 0) {
      const getRole = (ws: Workspace): WorkspaceRole => {
        if (rolesByWorkspaceId) {
          if (rolesByWorkspaceId instanceof Map) {
            const mapped = rolesByWorkspaceId.get(ws.id)
            if (mapped) return mapped
          } else if (typeof rolesByWorkspaceId === 'object' && ws.id in rolesByWorkspaceId) {
            return (rolesByWorkspaceId as Record<string, WorkspaceRole>)[ws.id]
          }
        }
        return ((ws as unknown as Record<string, unknown>).role as WorkspaceRole) ?? 'VIEWER'
      }

      const allHelper = workspaces.every((ws) => getRole(ws) === 'HELPER')
      return allHelper
    }
  }

  // 3. If activeRole is provided and is a non-helper role:
  if (activeRole && activeRole !== 'HELPER') {
    return false
  }

  // 4. If there are no active memberships or workspaces:
  // Phone-only identity is always a helper account
  if (currentUser && isPhoneOnlyIdentity(currentUser)) {
    return true
  }

  // For old accounts with userProfile.role === 'HELPER' and 0 memberships:
  // they remain helper-only unless they have non-helper memberships.
  if (userProfile?.role === 'HELPER') {
    return true
  }

  return false
}
